import React, { useEffect, useState } from 'react';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '../utils/password-policy';
import { cn } from '../utils/cn';
import { PasswordRequirementsHint } from './PasswordRequirementsHint';

const DEFAULT_BRAND_LOGO = '/ship-span-logo-dark.png?v=10';

function IconLock({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-7h-1V8a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2ZM9 8a3 3 0 0 1 6 0v2H9V8Zm9 12H6v-8h12v8Z" />
    </svg>
  );
}

function IconEye({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 5c5.5 0 9.5 4.5 10.5 6-.9 1.6-4.7 6-10.5 6S2.4 12.6 1.5 11C2.5 9.5 6.5 5 12 5Zm0 2C8.1 7 4.9 9.9 4 11c.9 1.1 4.1 4 8 4s7.1-2.9 8-4c-.9-1.1-4.1-4-8-4Zm0 1.5A2.5 2.5 0 1 1 9.5 11 2.5 2.5 0 0 1 12 8.5Z" />
    </svg>
  );
}

function IconEyeOff({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M2.1 3.5 3.5 2.1l18.4 18.4-1.4 1.4-3.1-3.1A12.6 12.6 0 0 1 12 19C6.2 19 2.4 14.6 1.5 13c.5-.9 2.1-3.1 4.7-4.8L2.1 3.5ZM12 7c3.9 0 7.1 2.9 8 4-.4.5-1.4 1.7-3 2.8l-2.1-2.1A2.5 2.5 0 0 0 12 8.5c-.2 0-.4 0-.6.1L9.7 6.9C10.4 7 11.2 7 12 7Zm-8 4c.4-.5 1.5-1.8 3.2-2.9l2.2 2.2A2.5 2.5 0 0 0 12 13.5c.3 0 .6-.1.9-.2l1.7 1.7c-.8.3-1.7.5-2.6.5-3.9 0-7.1-2.9-8-4Z" />
    </svg>
  );
}

function IconError({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 14h-2v-2h2v2Zm0-4h-2V7h2v5Z" />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2Zm-1.2 14.2-3.5-3.5 1.4-1.4 2.1 2.1 5.1-5.1 1.4 1.4-6.5 6.5Z" />
    </svg>
  );
}

function IconArrowBack({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20v-2Z" />
    </svg>
  );
}

function IconAnchor({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a3 3 0 0 1 1 5.8V10h3v2h-3v5.1A7 7 0 0 0 19 13h2a9 9 0 0 1-8 8.9V22h-2v-.1A9 9 0 0 1 3 13h2a7 7 0 0 0 6 4.1V12H8v-2h3V7.8A3 3 0 0 1 12 2Zm0 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" />
    </svg>
  );
}

export type AuthResetPasswordPageProps = {
  /** e.g. "Customer Portal", "Vendor Portal" */
  portalName: string;
  /** Imported image URL for the left hero */
  backgroundSrc: string;
  backgroundAlt?: string;
  brandLogoSrc?: string;
  apiFetchUrl: (path: string) => string;
  onNavigateLogin: () => void;
};

/**
 * Shared dark split-layout Reset Password screen used by all portals.
 * Only portal name + background image differ per app.
 */
export function AuthResetPasswordPage({
  portalName,
  backgroundSrc,
  backgroundAlt = 'Maritime operations',
  brandLogoSrc = DEFAULT_BRAND_LOGO,
  apiFetchUrl,
  onNavigateLogin,
}: AuthResetPasswordPageProps) {
  const [params] = useSearchParamsSafe();
  const token = params.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordOk = isPasswordStrong(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = Boolean(token) && passwordOk && passwordsMatch && !loading;

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!isPasswordStrong(newPassword)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(apiFetchUrl('/api/v1/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setTimeout(() => {
          onNavigateLogin();
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-reset-password-page h-screen flex flex-col bg-[#060d18] font-sans text-white overflow-hidden">
      <header className="relative z-30 flex justify-between items-center px-6 py-3 border-b border-white/5 backdrop-blur-md bg-[#060d18]/60 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={brandLogoSrc} alt="Ship Span ERP" className="w-9 h-9 object-contain" />
          <div className="flex flex-col">
            <h1 className="font-bold text-sm leading-tight tracking-wide text-white">Ship Span ERP</h1>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/50">Maritime Solutions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-white/70">
            <IconAnchor className="w-4 h-4 text-white/50" />
            <span className="font-medium">{portalName}</span>
          </div>
          <button
            type="button"
            onClick={onNavigateLogin}
            className="bg-[#0088cc] hover:bg-[#0077b3] text-white px-4 py-1.5 rounded-lg font-semibold text-xs transition-all flex items-center gap-1.5"
          >
            SIGN IN
          </button>
        </div>
      </header>

      <main className="relative z-20 flex-1 flex overflow-hidden m-3 rounded-2xl border border-white/[0.06]">
        <div className="hidden lg:flex lg:w-[55%] relative bg-[#0a1929] overflow-hidden">
          <img
            src={backgroundSrc}
            alt={backgroundAlt}
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70 animate-[slowPan_30s_linear_infinite]"
          />
          <div className="auth-reset-fade-base absolute inset-0 z-10 bg-gradient-to-r from-[#060d18]/55 via-[#060d18]/15 to-[#0b1523]" />
          <div className="auth-reset-fade-edge absolute inset-y-0 right-0 z-10 w-[55%] bg-gradient-to-r from-transparent via-[#0b1523]/55 to-[#0b1523]" />
          <div className="auth-reset-fade-top absolute inset-0 z-10 bg-gradient-to-t from-[#0a1929]/50 via-transparent to-[#0a1929]/35" />

          <div className="absolute inset-0 z-10 overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-[#0088cc] to-transparent animate-[scanLine_4s_linear_infinite]" />
          </div>

          <div className="relative z-20 flex flex-col justify-center h-full p-10 xl:p-14 pr-16 xl:pr-24">
            <div className="space-y-6 max-w-xl">
              <h2 className="text-5xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight">
                Set a New
                <br />
                <span className="text-[#0088cc]">Secure Password</span>
              </h2>
              <p className="text-base xl:text-lg text-white/65 leading-relaxed max-w-lg">
                Choose a strong password to protect your fleet account and keep procurement secure.
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2">
                {['Encrypted Link', 'Min 8 Characters', 'Instant Access'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#0088cc]" />
                    <span className="text-sm text-white/55 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-10 bg-[#0b1523] relative overflow-y-auto">
          <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzBhOGJjZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] pointer-events-none" />

          <div
            className={cn(
              'relative z-10 w-full max-w-lg',
              'rounded-2xl border border-white/[0.08] bg-[#0f1c2e]/90 backdrop-blur-md',
              'p-8 sm:p-10',
              'shadow-[0_24px_80px_rgba(0,0,0,0.55),0_0_0_1px_rgba(0,136,204,0.08),0_12px_40px_rgba(0,136,204,0.12)]',
            )}
          >
            {success ? (
              <div className="space-y-6 text-center">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mx-auto">
                  <IconCheck className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="space-y-3">
                  <h2 className="text-2xl font-bold text-white">Password Reset Successful</h2>
                  <p className="text-sm text-white/60">
                    Your password has been reset successfully. Redirecting to login...
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-8 text-center sm:text-left">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-black/40 border border-white/10 mb-4 overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
                    <img src={brandLogoSrc} alt="Ship Span ERP" className="w-11 h-11 object-contain" />
                  </div>
                  <h2 className="text-3xl font-bold text-white tracking-tight">Reset Password</h2>
                  <p className="text-sm text-white/60 mt-2">Enter your new password</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm animate-[shake_0.3s_ease-in-out]">
                      <IconError className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label
                      htmlFor="newPassword"
                      className="block text-xs font-semibold text-white/70 uppercase tracking-wider"
                    >
                      New Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <IconLock className="w-5 h-5 text-white/40 group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        id="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="auth-reset-input w-full pl-11 pr-12 py-3.5 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all"
                        placeholder="Min 8 chars, uppercase, number, symbol"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/80 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                      </button>
                    </div>
                    <PasswordRequirementsHint password={newPassword} dark />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="confirmPassword"
                      className="block text-xs font-semibold text-white/70 uppercase tracking-wider"
                    >
                      Confirm New Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <IconLock className="w-5 h-5 text-white/40 group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="auth-reset-input w-full pl-11 pr-12 py-3.5 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/80 transition-colors"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && (
                      <p
                        className={cn(
                          'text-[11px] mt-1.5',
                          passwordsMatch ? 'text-emerald-400' : 'text-red-300',
                        )}
                      >
                        {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>

                  <div className="pt-2 space-y-3">
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={cn(
                        'w-full py-3.5 rounded-xl text-sm font-semibold transition-all relative overflow-hidden',
                        'bg-[#0088cc] hover:bg-[#0099e6] text-white',
                        'shadow-lg shadow-[#0088cc]/25 hover:shadow-[#0088cc]/35',
                        'active:scale-[0.98]',
                        !canSubmit && 'opacity-70 cursor-not-allowed',
                      )}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite] pointer-events-none" />
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {loading ? (
                          <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Resetting...
                          </>
                        ) : (
                          'Reset Password'
                        )}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={onNavigateLogin}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-all"
                    >
                      <IconArrowBack className="w-4 h-4" />
                      Back to Login
                    </button>
                  </div>
                </form>
              </>
            )}

            <div className="mt-8 pt-5 border-t border-white/[0.06] text-center">
              <p className="text-[11px] text-white/40">Secure access to Ship Span ERP {portalName}</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-20 text-center py-2.5 border-t border-white/[0.04] bg-[#060d18]/60 flex-shrink-0">
        <p className="text-[11px] text-white/35">2026 &copy; Ship Span ERP. All Ships Served.</p>
      </footer>

      <style>{`
        @keyframes slowPan {
          0% { transform: scale(1.05) translateX(0) translateY(0); }
          50% { transform: scale(1.1) translateX(-1%) translateY(-0.5%); }
          100% { transform: scale(1.05) translateX(0) translateY(0); }
        }
        @keyframes scanLine {
          0% { top: 0%; }
          100% { top: 100%; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        /* Guaranteed dark + tall inputs even if portal CSS overrides load later */
        .auth-reset-password-page input.auth-reset-input {
          width: 100% !important;
          min-height: 3.75rem !important;
          height: 3.75rem !important;
          padding: 1rem 3rem 1rem 2.75rem !important;
          font-size: 0.9375rem !important;
          background: #0a1220 !important;
          background-color: #0a1220 !important;
          color: #fff !important;
          border: 1px solid rgba(255,255,255,0.18) !important;
          border-radius: 0.75rem !important;
        }
        .auth-reset-password-page input.auth-reset-input::placeholder {
          color: rgba(255,255,255,0.35) !important;
        }
        .auth-reset-password-page input.auth-reset-input:focus {
          background: #0c1628 !important;
          border-color: rgba(0,136,204,0.65) !important;
        }
      `}</style>
    </div>
  );
}

/** Local URLSearchParams hook so shared UI does not depend on react-router. */
function useSearchParamsSafe(): [URLSearchParams] {
  const [params, setParams] = useState(() =>
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams(),
  );

  useEffect(() => {
    const sync = () => setParams(new URLSearchParams(window.location.search));
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  return [params];
}
